/**
 * Stage4ProjectTour — US-07-08
 *
 * Tour deep-dive workflow dịch: upload → preflight → translate → Cấu hình tab
 * với preset ngành → Thuật ngữ tab.
 *
 * Trigger: user mở project detail lần đầu + `stage4_completed` chưa set.
 * Gate behind: stage 1 (US-07-04) đã complete (`tour_completed = '1'`).
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { driver, type Driver } from 'driver.js';
import 'driver.js/dist/driver.css';
import './tour-driver-overrides.css';
import { useTourStore } from '../../../store/useTourStore';
import { useProjectStore } from '../../../store/useProjectStore';
import { DEMO_PROJECT_ID, DEMO_TERMS } from '../../../utils/demoProject';

const STORAGE_KEY_STAGE4 = 'tour_stage4_completed';
const STORAGE_KEY_MAIN = 'tour_completed';

interface Props {
  isInProjectDetail: boolean;
  projectIndustry?: string | null;
  /** Gọi sau khi tour kết thúc — thường dùng để navigate back về projects list */
  onComplete?: () => void;
}

const STAGE4_STEPS = (industry: string | null | undefined) => [
  {
    element: '[data-tour="dropzone"]',
    popover: {
      title: '📂 Nạp tài liệu',
      description: `
        Bấm đây để upload tài liệu cần dịch — hỗ trợ <b>Word, PowerPoint, TXT</b>.<br><br>
        App tự parse và <b>giữ nguyên định dạng</b> bảng, chart, SmartArt.
        File sau khi nạp sẽ hiện trong bảng bên dưới.
      `,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="file-view-btn"]',
    popover: {
      title: '👁️ Xem bản dịch đối chiếu',
      description: `
        Bấm icon mắt để mở <b>bảng so sánh song ngữ</b> — đoạn gốc bên trái, bản dịch bên phải.<br><br>
        Sau khi dịch xong, bạn có thể <b>chỉnh sửa trực tiếp</b> từng đoạn trong bảng này trước khi xuất file.
      `,
      side: 'right' as const,
    },
  },
  {
    element: '[data-tour="file-credit"]',
    popover: {
      title: '💳 Chi phí dịch',
      description: `
        Con số này là <b>chi phí chính xác</b> cho file này — được tính bằng Gemini Token API, không sai lệch.<br><br>
        Credit chỉ bị trừ khi bạn bấm <b>Dịch</b> — không trừ khi upload.
      `,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="file-translate-btn"]',
    popover: {
      title: '⚡ Dịch — chạy AI cho file này',
      description: `
        Bấm để dịch <b>riêng file này</b>. AI dịch từng đoạn, giữ nguyên văn phong.<br><br>
        Kết quả hiện ngay trong bảng — bấm <b>icon mắt</b> bên trái để xem bản đối chiếu.
      `,
      side: 'left' as const,
    },
  },
  {
    element: '[data-tour="translate"]',
    popover: {
      title: '⚡ Dịch tất cả — chạy toàn hàng đợi',
      description: `
        Khi có nhiều file, bấm đây để dịch <b>tất cả cùng lúc</b>.<br><br>
        App hiện modal xác nhận tổng credit trước khi chạy — không bị trừ nhầm.
      `,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="file-qc-toggle"]',
    popover: {
      title: '🛡️ Agent Kiểm Tra',
      description: `
        Tick vào đây để <b>Agent AI tự chấm bản dịch</b> sau khi dịch xong.<br><br>
        Đánh giá 5 tiêu chí: Chính xác · Thuật ngữ · Lưu loát · Đầy đủ · Văn phong.<br>
        Tốn thêm ~20% credit nhưng tiết kiệm thời gian rà soát thủ công.
      `,
      side: 'left' as const,
    },
  },
  {
    element: '[data-tour="tab-settings"]',
    onHighlighted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '⚙️ Tab Cấu hình',
      description: `Tab này chứa toàn bộ cài đặt riêng cho project — ngôn ngữ, chỉ thị AI, định dạng xuất và nút lưu.<br><br>Hãy xem qua từng mục bên dưới.`,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="settings-name"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '📝 Tên dự án',
      description: `Đổi tên dự án ở đây — tên hiển thị trên danh sách và dùng để đặt tên file xuất.<br><br>Nên đặt tên rõ ràng theo nội dung, VD: "Báo cáo thường niên 2025 - VDSC".`,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="settings-lang"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '🌐 Cặp ngôn ngữ',
      description: `Chọn <b>ngôn ngữ gốc</b> của tài liệu và <b>ngôn ngữ đích</b> muốn dịch sang.<br><br>Thay đổi tại đây áp dụng cho toàn bộ file trong project này.`,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="settings-prompt"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '✍️ Chỉ thị AI',
      description: `Hướng dẫn phong cách dịch cho AI — trang trọng, kỹ thuật, hay thân thiện.<br><br>${industry ? `Project đang dùng preset <b>${industry}</b>. ` : ''}Bấm <b>"Chọn preset ngành"</b> để đổi template theo 6 ngành: tài chính, pháp lý, y tế...`,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="settings-qc-lang"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '🛡️ Ngôn ngữ Agent Kiểm Tra',
      description: `Chọn ngôn ngữ Agent AI dùng để viết báo cáo chấm điểm bản dịch.<br><br>Mặc định là <b>Tiếng Việt</b> — phù hợp nếu bạn muốn đọc phản hồi bằng tiếng Việt.`,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="settings-locale"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '🗓️ Định dạng số & ngày',
      description: `AI sẽ dùng định dạng này khi dịch ngày tháng và số trong tài liệu.<br><br>VD: <b>Việt Nam</b> → "15/03/2025", <b>Mỹ</b> → "03/15/2025". Chọn <b>Tự động</b> để theo ngôn ngữ đích.`,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="settings-save"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-settings"]') as HTMLElement)?.click();
    },
    popover: {
      title: '💾 Lưu thay đổi',
      description: `Sau khi chỉnh sửa bất kỳ cài đặt nào, bấm <b>"Lưu thay đổi"</b> để áp dụng.<br><br>Nếu không lưu, mọi thay đổi sẽ mất khi chuyển tab khác.`,
      side: 'top' as const,
    },
  },
  {
    element: '[data-tour="tab-terminology"]',
    onHighlighted: () => {
      (document.querySelector('[data-tour="tab-terminology"]') as HTMLElement)?.click();
    },
    popover: {
      title: '📚 Tab Thuật ngữ',
      description: `Từ điển riêng của project — khác với "Từ điển chung" trên sidebar, glossary này <b>chỉ áp dụng project này</b>.<br><br>Hãy xem các tính năng bên dưới.`,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="term-add"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-terminology"]') as HTMLElement)?.click();
    },
    onHighlighted: () => {
      window.dispatchEvent(new CustomEvent('tour-open-add-menu'));
    },
    popover: {
      title: '➕ Thêm thuật ngữ — 2 cách',
      description: `
        <b>1. Thủ công</b> — nhập từ gốc + bản dịch + danh mục. Hỗ trợ nhiều cách viết gốc (VD: "HĐQT", "Hội đồng quản trị") và nhiều bản dịch.<br><br>
        <b>2. Excel</b> — kéo thả .xlsx (cột A: gốc, B: dịch, C: danh mục).<br><br>
        Sau khi thêm, AI tự động áp dụng vào mọi bản dịch.
      `,
      side: 'top' as const,
      align: 'end' as const,
    },
  },
  {
    element: '[data-tour="term-export"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-terminology"]') as HTMLElement)?.click();
    },
    onHighlighted: () => {
      window.dispatchEvent(new CustomEvent('tour-open-export-menu'));
    },
    popover: {
      title: '⬇️ Xuất — share & backup',
      description: `
        <b>📊 Excel (.xlsx)</b> — toàn bộ glossary ra file Excel. Đồng nghiệp <b>import ngược lại</b> được (qua nút Thêm mới → Excel), tiện edit hàng loạt + backup.<br><br>
        <b>📚 Đẩy lên Từ điển chung</b> — share glossary cho mọi project khác trong account dùng chung.
      `,
      side: 'top' as const,
      align: 'end' as const,
    },
  },
  {
    element: '[data-tour="term-search"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-terminology"]') as HTMLElement)?.click();
      window.dispatchEvent(new CustomEvent('tour-close-menus'));
    },
    popover: {
      title: '🔍 Tìm kiếm thuật ngữ',
      description: `Khi glossary nhiều thuật ngữ, dùng ô tìm kiếm để lọc nhanh — tìm theo <b>từ gốc</b> hoặc <b>bản dịch</b>.<br><br>Hỗ trợ tìm partial: gõ "doanh" sẽ ra cả "doanh thu", "doanh nghiệp"...`,
      side: 'bottom' as const,
    },
  },
  {
    element: '[data-tour="term-list"]',
    onHighlightStarted: () => {
      (document.querySelector('[data-tour="tab-terminology"]') as HTMLElement)?.click();
      window.dispatchEvent(new CustomEvent('tour-close-menus'));
    },
    popover: {
      title: '📋 Bảng thuật ngữ mẫu',
      description: `
        Đây là <b>7 thuật ngữ mẫu</b> đã được nạp sẵn cho project demo — gồm tài chính, pháp lý, nhân sự và tên tổ chức (Eximbank, Ngân hàng Nhà nước...).<br><br>
        Mỗi thuật ngữ có thể có <b>nhiều cách viết gốc</b> (VD: "HĐQT" và "Hội đồng quản trị") và <b>nhiều bản dịch</b> (đầy đủ + viết tắt).<br><br>
        AI sẽ <b>tự động dùng các thuật ngữ này</b> khi dịch — bản dịch nhất quán, đúng chuẩn ngành.
      `,
      side: 'top' as const,
    },
  },
  {
    popover: {
      title: '🎉 Chúc mừng bạn!',
      description: `
        Bạn đã nắm hết các tính năng chính của <b>Dịch Tài Liệu</b>!<br><br>
        Giờ hãy tạo project thật của bạn — tải lên tài liệu đầu tiên và trải nghiệm dịch thuật AI chuyên nghiệp.<br><br>
        <span style="font-size:11px;color:#94a3b8">Tip: Ghé tab <b>Thuật ngữ</b> để thêm từ chuyên ngành trước khi dịch — bản dịch sẽ chính xác hơn nhiều.</span>
      `,
      doneBtnText: 'Tạo project ngay 🚀',
    },
  },
];

const Stage4ProjectTour: React.FC<Props> = ({ isInProjectDetail, projectIndustry, onComplete }) => {
  const driverRef = useRef<Driver | null>(null);
  const triggeredRef = useRef(false);
  const forceNextRef = useRef(false);
  // Ref để tránh onComplete vào deps của useCallback → gây restart tour khi parent re-render
  const onCompleteRef = useRef(onComplete);
  useEffect(() => { onCompleteRef.current = onComplete; }, [onComplete]);
  const tourStep = useTourStore(s => s.step);

  const startTour = useCallback(() => {
    driverRef.current = driver({
      showProgress: false,
      nextBtnText: 'Tiếp',
      prevBtnText: 'Trước',
      doneBtnText: 'Đã hiểu',
      allowClose: true,
      disableActiveInteraction: true,
      onPopoverRender: (popover) => {
        // Inject nút "Bỏ qua hướng dẫn" vào footer-trái (giữ Trước/Tiếp bên phải)
        if (!popover.footer || popover.footer.querySelector('.driver-skip-btn')) return;
        const skipBtn = document.createElement('button');
        skipBtn.type = 'button';
        skipBtn.className = 'driver-skip-btn';
        skipBtn.textContent = 'Bỏ qua hướng dẫn';
        skipBtn.onclick = () => driverRef.current?.destroy();
        popover.footer.prepend(skipBtn);
      },
      onDestroyed: () => {
        localStorage.setItem(STORAGE_KEY_STAGE4, '1');
        if (useTourStore.getState().step === 'in-detail') {
          useTourStore.getState().end();
        }
        onCompleteRef.current?.();
      },
      steps: STAGE4_STEPS(projectIndustry),
    });
    // Patch demo project nếu thuật ngữ bị thiếu (session cũ / replay qua nút ?)
    // TerminologyTab đọc từ importedTerminology, badge counter đọc từ synonymGroups → set cả 2.
    const store = useProjectStore.getState();
    const demoProj = store.projects.find(p => p.id === DEMO_PROJECT_ID);
    if (demoProj && (!demoProj.importedTerminology || demoProj.importedTerminology.length === 0)) {
      store.updateProject({ ...demoProj, synonymGroups: DEMO_TERMS, importedTerminology: DEMO_TERMS });
    }
    driverRef.current.drive();
  }, [projectIndustry]); // onComplete removed — dùng ref để tránh re-create startTour

  useEffect(() => {
    if (!isInProjectDetail) return;
    if (triggeredRef.current) return;

    const force = forceNextRef.current;
    if (!force) {
      const mainDone = localStorage.getItem(STORAGE_KEY_MAIN) === '1';
      const stage4Done = localStorage.getItem(STORAGE_KEY_STAGE4) === '1';
      if (!mainDone || stage4Done) return;
    }

    triggeredRef.current = true;
    forceNextRef.current = false;
    // Delay 1.2s để UI project detail render xong + user có thời gian nhìn
    const t = setTimeout(startTour, 1200);
    return () => clearTimeout(t);
  }, [isInProjectDetail, startTour]);

  // Tutorial mode: khi step = 'in-detail' (user click demo row trong ProjectsList tour),
  // fire Stage4 trực tiếp — không cần localStorage gates hay forceNextRef.
  useEffect(() => {
    if (tourStep !== 'in-detail' || !isInProjectDetail) return;
    triggeredRef.current = true;
    forceNextRef.current = false;
    localStorage.removeItem(STORAGE_KEY_STAGE4);
    const t = setTimeout(() => startTour(), 900);
    return () => clearTimeout(t);
  }, [tourStep, isInProjectDetail, startTour]);

  // Listener: nút "?" trên ProjectsList dispatch event để replay tour
  useEffect(() => {
    const handler = () => {
      triggeredRef.current = false;
      forceNextRef.current = true;
      localStorage.removeItem(STORAGE_KEY_STAGE4);
      if (isInProjectDetail) {
        triggeredRef.current = true;
        forceNextRef.current = false;
        setTimeout(() => startTour(), 1200);
      }
    };
    window.addEventListener('restart-stage4-tour', handler);
    return () => window.removeEventListener('restart-stage4-tour', handler);
  }, [isInProjectDetail, startTour]);

  return null;
};

export default Stage4ProjectTour;
